import { z } from "zod";
import { createEndpoint, createEndpointWithPagination } from "../createEndpoint";
import { HttpClient, QueryParams } from "../httpClient";
import {
    TokenValidationSchema,
    PublicReviewSchema,
    PublicReviewsStatsSchema,
    ReviewSubmissionResponseSchema,
    ClientRegistrationResponseSchema,
    LatestReviewsResponseSchema,
    LatestReviewSchema,
} from "../schemas/reviews-v2";

// Re-export types for consumer convenience
export type LatestReview = z.infer<typeof LatestReviewSchema>;
export type LatestReviewsResponse = z.infer<typeof LatestReviewsResponseSchema>;

export interface PublicExpertReviewsQueryParams extends QueryParams {
    limit?: number;
    cursor?: string;
}

export interface SubmitPublicReviewBody {
    token: string;
    quality: number; // 1-5
    communication: number; // 1-5
    timelines: number; // 1-5
    value_for_money: number; // 1-5
    review_text: string; // min 10 characters
    is_anonymous?: boolean;
    project_value_range_id?: number;
}

export interface ClientRegistrationBody {
    email: string;
    password: string; // min 8 characters
    first_name: string;
    last_name: string;
    company_name: string;
    company_website?: string;
}

export function createPublicReviewsV2Api(http: HttpClient, baseUrl: string) {
    return {
        validateToken: createEndpoint<
            z.infer<typeof TokenValidationSchema>,
            unknown,
            QueryParams,
            { token: string }
        >(http, "GET", "/public/reviews/validate-token/:token", {
            base: baseUrl,
            responseSchema: TokenValidationSchema,
            params: ["token"],
        }),

        submitReview: createEndpoint<
            z.infer<typeof ReviewSubmissionResponseSchema>,
            SubmitPublicReviewBody,
            QueryParams
        >(http, "POST", "/public/reviews", {
            base: baseUrl,
            responseSchema: ReviewSubmissionResponseSchema,
        }),

        getExpertReviews: createEndpointWithPagination<
            z.infer<typeof PublicReviewSchema>,
            z.infer<typeof PublicReviewsStatsSchema>,
            unknown,
            PublicExpertReviewsQueryParams,
            { slug: string }
        >(http, "GET", "/public/experts/:slug/reviews", {
            base: baseUrl,
            itemSchema: PublicReviewSchema,
            metaSchema: PublicReviewsStatsSchema,
            params: ["slug"],
        }),

        /**
         * Get the latest high-rated reviews (4.5+ rating)
         * Returns up to 10 most recent published reviews across all experts
         */
        getLatestReviews: createEndpoint<
            z.infer<typeof LatestReviewsResponseSchema>,
            unknown,
            QueryParams
        >(http, "GET", "/public/reviews/latest", {
            base: baseUrl,
            responseSchema: LatestReviewsResponseSchema,
        }),

        registerClient: createEndpoint<
            z.infer<typeof ClientRegistrationResponseSchema>,
            ClientRegistrationBody,
            QueryParams
        >(http, "POST", "/client/register", {
            base: baseUrl,
            responseSchema: ClientRegistrationResponseSchema,
        }),
    };
}

